import {
  FormattedPlanDetails,
  LanguageNameSpaces,
  SubscriptionDuration,
  SubscriptionPlanType,
} from "@shared/types";
import { useUser } from "@shared/utils/hooks/misc.ts";
import { useContext, useState } from "react";
import { useCheckQuotaUsage } from "@shared/features/settings/hooks.ts";
import {
  convertPriceToMonthlyPrice,
  getPlanDiscount,
  getPlanPercentageDiscount,
  getPlanPrice,
} from "@shared/features/settings/subscription/utils.ts";
import moment from "moment";
import { dateToMoment, formatDate } from "@shared/utils/date.js";
import { GetQuotaPlanFeatureType } from "@shared/features/settings/subscription/types.ts";
import { useGeneralPaymentErrors } from "features/settings/common/hooks.tsx";
import { openMessage } from "utils/misc";
import { IS_WEB_VIEW } from "common/constants.ts";
import usePlanFeatures from "@shared/features/settings/subscription/hooks/usePlanFeatures";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { BuySubscriptionContext } from "features/settings/Subscription/BuySubscription.context.tsx";

interface usePlanCardProps {
  plan: FormattedPlanDetails;
  isUpgrade?: boolean;
  promoCodeDiscount?: Record<SubscriptionDuration, number>;
  getQuotaPlanFeature: GetQuotaPlanFeatureType;
  onUpgradeClick: () => void;
}

const usePlanCard = ({
  plan,
  isUpgrade,
  promoCodeDiscount,
  getQuotaPlanFeature,
  onUpgradeClick,
}: usePlanCardProps) => {
  const { plans, setSubscriptionType, showUpgradePrices, subscriptionParams } =
    useContext(BuySubscriptionContext)!;
  const { t } = useTypedTranslation(LanguageNameSpaces.BILLING);
  const [downgradeQuotaModalOpen, setDowngradeQuotaModalOpen] = useState(false);
  const [confirmDowngradeModalOpen, setConfirmDowngradeModalOpen] =
    useState(false);
  const [autoRenewalModalOpen, setAutoRenewalModalOpen] = useState(false);
  const [cancelAutoRenewalModalOpen, setCancelAutoRenewalModalOpen] =
    useState(false);
  const isFreePlan = plan.subscription_plan_type === SubscriptionPlanType.FREE;
  const queryDuration = subscriptionParams.duration;
  const showFreeTag = isFreePlan && queryDuration === 360;

  const { checkQuotaUsage, allQuotaUsage, isLoading, getExtraQuota } =
    useCheckQuotaUsage();
  const { getPlanFeatures, selectedPlanUsageLimits } = usePlanFeatures({
    plan,
    getQuotaPlanFeature,
  });
  const warningMessage = useGeneralPaymentErrors({ plan });

  const { user } = useUser();
  const isDowngradePlan =
    plan.subscription_plan_type &&
    user?.subscription_plan_type &&
    plan.subscription_plan_type < user.subscription_plan_type;
  const isCurrentPlan =
    plan?.subscription_plan_type === user?.subscription_plan_type;
  const isUpgradePlan =
    !isDowngradePlan && !isCurrentPlan && !user?.isMaxSubscription();

  const planDiscount = getPlanPercentageDiscount(plan, queryDuration);
  const isPromoCodeDiscount =
    !!promoCodeDiscount &&
    (planDiscount || 0) / (promoCodeDiscount[queryDuration] || 0) > 0.95;

  const isPopular =
    plan.subscription_plan_type === SubscriptionPlanType.ADVANCED;
  const isPremium =
    plan.subscription_plan_type === SubscriptionPlanType.PREMIUM;

  const handleSelectSubscription = () => {
    if (plan.subscription_plan_type) {
      setSubscriptionType(plan.subscription_plan_type);
    }
    setConfirmDowngradeModalOpen(false);
  };

  const [_, discountPercent, yearlyAmountDiscount] =
    !isUpgrade && plan.subscription_plan_type
      ? getPlanDiscount(
          plans,
          plan.subscription_plan_type,
          queryDuration,
          undefined
        )
      : [];

  const upgradePlan = plan.upgradePlan;
  const planDuration = isUpgrade
    ? showUpgradePrices
      ? upgradePlan?.days_left
      : upgradePlan?.subscription_days
    : queryDuration;
  const upgradePrice = showUpgradePrices
    ? upgradePlan?.upgrade_price
    : planDuration
    ? getPlanPrice(plan[planDuration])
    : 0;

  const getMonthlyPrice = () => {
    if (planDuration) {
      const price = showUpgradePrices
        ? upgradePrice
        : getPlanPrice(plan[planDuration]);
      return convertPriceToMonthlyPrice(price, planDuration);
    }
    return 0;
  };

  const expirationAfter14Days =
    moment().diff(dateToMoment(user?.expiration_timestamp, true), "days") > 14;
  const formattedExpiration = user?.expiration_timestamp
    ? formatDate(user?.expiration_timestamp, false, true)
    : undefined;

  const getPurchaseButtonText = () => {
    let baseButtonText =
      isUpgrade || user?.isFree()
        ? t("plan_card_buttons.upgrade_now")
        : t("plan_card_buttons.buy_now");
    if (subscriptionParams.isAllPlans) {
      if (isDowngradePlan) baseButtonText = t("plan_card_buttons.downgrade");
      else if (isCurrentPlan) baseButtonText = t("plan_card_buttons.extend");
      else if (isUpgradePlan) baseButtonText = t("plan_card_buttons.upgrade");
    }
    if (isFreePlan) {
      baseButtonText = isCurrentPlan
        ? t("plan_card_buttons.active")
        : t("plan_card_buttons.downgrade");
    }
    return baseButtonText;
  };

  const onBuyClick = () => {
    if (warningMessage) {
      openMessage(warningMessage, "warning");
      return;
    }
    const shouldCheckQuota =
      !isCurrentPlan &&
      ((isDowngradePlan &&
        (!user?.isExpired() || user?.in_view_only_grace_period)) ||
        (!isUpgradePlan && !isFreePlan));
    if (shouldCheckQuota) {
      checkQuotaUsage(plan.subscription_plan_type)?.then((hasPassedQuota) => {
        if (hasPassedQuota) {
          setDowngradeQuotaModalOpen(true);
        } else {
          if (IS_WEB_VIEW) {
            handleSelectSubscription();
          } else {
            setConfirmDowngradeModalOpen(true);
          }
        }
      });
    } else if (
      !user?.isFree() &&
      subscriptionParams.isAllPlans &&
      isUpgradePlan
    ) {
      onUpgradeClick();
    } else {
      handleSelectSubscription();
    }
  };

  const confirmDowngradeModalProps = {
    isOpen: confirmDowngradeModalOpen,
    closeModal: () => setConfirmDowngradeModalOpen(false),
    onOk: handleSelectSubscription,
    content: [
      t("confirm_downgrade_modal.immediate_downgrade_message", {
        date: formattedExpiration,
      }),
      expirationAfter14Days
        ? t("confirm_downgrade_modal.purchase_closer_message")
        : "",
    ],
  };

  const autoRenewalModalProps = {
    isOpen: autoRenewalModalOpen,
    closeModal: () => setAutoRenewalModalOpen(false),
    content: [
      t("auto_renewal_modal.cannot_purchase"),
      t("auto_renewal_modal.disable_first"),
    ],
    footerData: {
      secondaryButton: {
        text: t("auto_renewal_modal.cancel_auto_renewal"),
        onClick: () => {
          setAutoRenewalModalOpen(false);
          setCancelAutoRenewalModalOpen(true);
        },
      },
      primaryButton: {
        text: t("auto_renewal_modal.ok"),
        onClick: () => setAutoRenewalModalOpen(false),
      },
    },
  };

  const downgradeQuotaModalProps = {
    isOpen: downgradeQuotaModalOpen,
    onCancel: () => setDowngradeQuotaModalOpen(false),
    currentQuotaUsage: allQuotaUsage,
    selectedPlanUsageLimits,
    planType: plan.subscription_plan_type,
  };

  const cancelSubscriptionModalProps = {
    visible: cancelAutoRenewalModalOpen,
    closeModal: () => setCancelAutoRenewalModalOpen(false),
  };

  return {
    user,
    isLoading,
    getExtraQuota,
    isCurrentPlan,
    isDowngradePlan: !!isDowngradePlan,
    upgradePrice,
    isPromoCodeDiscount,
    isPopular,
    isPremium,
    yearlyAmountDiscount,
    discountPercent,
    getMonthlyPrice,
    getPurchaseButtonText,
    getPlanFeatures,
    onBuyClick,
    confirmDowngradeModalProps,
    autoRenewalModalProps,
    downgradeQuotaModalProps,
    cancelSubscriptionModalProps,
    isFreePlan,
    showFreeTag,
    isErrorButton: !!warningMessage,
  };
};

export default usePlanCard;
